import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { OrderTimeLine } from './OrderTimeLine';
import { OrderEstimates } from './OrderEstimates';
import { OrderProducts } from './OrderProducts';
import { OrderInfoCard } from './OrderInfoCard';
import { OrderAdditionalDetails } from './OrderAdditionalDetails';
import { OrderDetailsHeader } from './OrderDetailsHeader';
import { OrderActions } from './OrderActions';
import { PanelTopBar } from './Styled';
import {
  OrderActionsSkeleton,
  OrderCardSkeleton,
  OrderMetaSkeleton,
  OrderProductsSkeleton,
  OrderTitleSkeleton,
} from './OrderDetailsSkeleton';
import { InlineErrorMessage } from '../../InlineErrorMessage/InlineErrorMessage';
import { Button, LinearProgress } from '@mui/material';
import { useSingleOrder } from '../../../apis/orders-api';
import { ORDERS_READ, usePermission } from '../../utils/permissions';
import { isForbiddenError } from '../../utils/apiErrors';
import { ForbiddenNote } from '../../PermissionNote/ForbiddenNote';

// The same permission `OrderAdditionalDetails` gates its card on.
const OTHER_DETAILS_PERMISSIONS = [ORDERS_READ];

type OrderDetailsViewProps = {
  orderId: number;
  /**
   * `panel` drops the wide-screen two-column layout: inside `OrderDetailsDrawer` the room available is
   * the panel's ~640px, which the viewport breakpoints know nothing about. It is also what pins the
   * title row, which only a panel with its own scroller can do.
   */
  variant?: 'page' | 'panel';
  /**
   * Placed on the title row — the panel passes its close button, the full page passes nothing. It is
   * rendered while the order is still loading too, so the panel is never open without a way out.
   */
  headerAction?: React.ReactNode;
};

/**
 * Everything the order detail page shows, addressed by order id rather than by route params, so the
 * same view can also be rendered inside the details panel the orders lists open.
 */
export const OrderDetailsView: React.FC<OrderDetailsViewProps> = ({ orderId, variant = 'page', headerAction }) => {
  const { t } = useTranslation();

  const { data: order, isLoading: isOrderLoading, isFetching, isError, error, refetch } = useSingleOrder(orderId);

  const isPanel = variant === 'panel';
  // The API decides who may read an order; this only reports the decision. Checking the returned
  // order's restaurant against this client's own idea of the user's shops was tried and removed: it
  // could only ever fire where the API had said yes and we said no, and hiding an order the server
  // deliberately returned helps nobody — least of all the agent on the phone to the customer who
  // placed it.
  const isForbidden = isForbiddenError(error);
  // Read here only so the skeleton's right column holds the same cards the real one will — the details
  // card itself decides whether to render.
  const canViewOtherDetails = usePermission(OTHER_DETAILS_PERMISSIONS);

  // Ahead of the error branch, because a refusal is an answer rather than a failure: offering "try
  // again" for it would send the reader round a loop that cannot turn "you may not" into "you may".
  if (isForbidden) {
    return (
      <Layout>
        {headerAction && (
          <PanelTopBar $sticky={isPanel} $raised={false}>
            {headerAction}
          </PanelTopBar>
        )}
        <ForbiddenNote />
      </Layout>
    );
  }

  // Only with nothing to show: a refresh that fails on an order already on screen is reported below
  // instead, since the order it failed to update is still worth reading.
  //
  // Nothing to pin against yet in either branch below, so no shadow: there is no content under the bar
  // to lift it off.
  if (isError && !order) {
    return (
      <Layout>
        {headerAction && (
          <PanelTopBar $sticky={isPanel} $raised={false}>
            {headerAction}
          </PanelTopBar>
        )}
        <InlineErrorMessage>{t('orderDetails.loadError')}</InlineErrorMessage>
        {/* The query does not retry on its own — `refetch` is the only way back from here. */}
        <RetryRow>
          <Button variant="outlined" onClick={() => refetch()}>
            {t('global.tryAgain')}
          </Button>
        </RetryRow>
      </Layout>
    );
  }

  if (!order || isOrderLoading) {
    return (
      // `aria-busy` rather than placeholder text: the skeletons below are `aria-hidden`, so a screen
      // reader hears "busy" instead of a dozen empty rows.
      <Layout aria-busy>
        <PanelTopBar $sticky={isPanel} $raised={false}>
          <OrderTitleSkeleton />
          {headerAction}
        </PanelTopBar>
        <OrderMetaSkeleton />
        {/* Empty, but reserved: the skeletons already say the order is on its way, and keeping the slot
            here is what puts the cards at the same distance from the header as they are once it lands. */}
        <ProgressSlot />

        {/* The real layout, filled with the real cards' shapes and in their order, so the content lands
            where its placeholder was instead of moving the column it is in. */}
        <OrderContainer $panel={isPanel}>
          <LeftColumn $panel={isPanel}>
            <OrderCardSkeleton rows={6} />
            <OrderProductsSkeleton title={t('Products')} />
          </LeftColumn>
          <RightColumn $panel={isPanel}>
            <OrderCardSkeleton title={t('Time estimates')} rows={3} />
            <OrderCardSkeleton title={t('Timeline')} rows={4} />
            {canViewOtherDetails && <OrderCardSkeleton title={t('Other details')} rows={3} />}
            <OrderActionsSkeleton title={t('Actions')} />
          </RightColumn>
        </OrderContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <OrderDetailsHeader order={order} action={headerAction} sticky={isPanel} />

      {/* Always rendered, so a refresh starting does not nudge the cards down by the bar's height. The
          order stays on screen while it happens — it is still the last true answer. */}
      <ProgressSlot>{isFetching && <LinearProgress aria-label={t('global.loading')} />}</ProgressSlot>

      {/* A refresh that failed. The cards below are the last answer that arrived, not a stale render of
          nothing, so they stay — this says only that they may no longer be current. */}
      {isError && <InlineErrorMessage>{t('orderDetails.loadError')}</InlineErrorMessage>}

      <OrderContainer $panel={isPanel}>
        <LeftColumn $panel={isPanel}>
          <OrderInfoCard order={order} />
          <OrderProducts order={order} />
        </LeftColumn>
        <RightColumn $panel={isPanel}>
          <OrderEstimates order={order} />
          <OrderTimeLine order={order} />
          <OrderAdditionalDetails order={order} />
          <OrderActions order={order} />
        </RightColumn>
      </OrderContainer>
    </Layout>
  );
};

/**
 * The column the header rows and the cards sit in, and the containing block the header's pinned row and
 * its sentinel measure themselves against — it has to span the whole scrolled content for the row to stay
 * pinned for the whole of the scroll, which is why the header is not wrapped in a box of its own.
 *
 * The gap is the small one, between the title row and the status below it. The distance from there down
 * to the cards is this gap twice over, with the refresh bar's reserved slot between the two.
 */
const Layout = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

/**
 * Room for the refresh bar, always reserved. 4px is `LinearProgress`'s own height, and together with the
 * layout column's gaps it makes up the whole distance from the header down to the cards — which is why
 * `OrderContainer` has no margin of its own.
 */
const ProgressSlot = styled.div`
  height: 4px;
`;

/** The retry button, on a row of its own under the message rather than beside it. */
const RetryRow = styled.div`
  display: flex;
`;

/**
 * The layout below is driven by viewport width, which is the wrong measure inside the details panel:
 * the panel is ~640px wide however wide the screen is. `$panel` keeps those rows and splits switched
 * off, so the panel always reads as the one-column phone layout.
 */
type PanelProp = { $panel: boolean };

// Stacks until md rather than sm: at 600-899px a 65/35 split leaves the right column ~200px, which is
// too narrow for the timeline and the estimates.
const OrderContainer = styled.div<PanelProp>`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${({ theme, $panel }) =>
    !$panel &&
    css`
      ${theme.breakpoints.up('md')} {
        flex-direction: row;
      }
    `}
`;

// `min-width: 0` lets long product names and URLs shrink the column instead of overflowing it.
const LeftColumn = styled.div<PanelProp>`
  width: 100%;
  min-width: 0;

  ${({ theme, $panel }) =>
    !$panel &&
    css`
      ${theme.breakpoints.up('md')} {
        width: 65%;
      }
    `}
`;
const RightColumn = styled.div<PanelProp>`
  width: 100%;
  min-width: 0;

  ${({ theme, $panel }) =>
    !$panel &&
    css`
      ${theme.breakpoints.up('md')} {
        margin-left: 20px;
        width: 35%;
      }
    `}
`;
